import { useSelector } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import useSignalsEventsQuery from "app/pages/sales-intelligence/modules/company-analysis/queries/useSignalsEventsQuery";
import { resolvePrimarySignalHook } from "app/pages/sales-intelligence/modules/company-analysis/services/signals/signalHooks";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import LoaderGradient from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Loader/LoaderGradient";
import SignalEventEmptyState from "app/pages/sales-intelligence/pages/company-analysis/components/signals/SignalEventEmptyState/SignalEventEmptyState";
import SignalEventFilter from "app/pages/sales-intelligence/pages/company-analysis/components/signals/SignalEventFilter/SignalEventFilter";
import SignalEventListHeader from "app/pages/sales-intelligence/pages/company-analysis/components/signals/SignalEventListHeader/SignalEventListHeader";
import SignalEventListItem from "app/pages/sales-intelligence/pages/company-analysis/components/signals/SignalEventListItem/SignalEventListItem";
import { StyledSignalWrapper } from "app/pages/sales-intelligence/pages/company-analysis/components/signals/SignalEventListItem/styles";
import SignalPromoBanner from "app/pages/sales-intelligence/pages/company-analysis/components/signals/SignalPromoBanner/SignalPromoBanner";
import type { TrackingProps } from "app/pages/sales-intelligence/pages/company-analysis/containers/signals/SignalEventList/withSignalEventsTracking";
import {
    selectNumberOfSignalsFromSource,
    selectSignalsDescriptionIsBeingFetched,
} from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";

import {
    StyledSignalIntentWrapper,
    StyledSignalsList,
    StyledSignalsListShowMoreLabel,
    StyledSignalsListShowMoreLayout,
} from "./styles";
import useShowMoreSignalEvents from "./useShowMoreSignalEvents";
import useSignalEventsFilter from "./useSignalEventsFilter";

type SignalEventListProps = {
    title: string;
    hasShowMoreButton?: boolean;
};

const DEFAULT_BANNER_POSITION = 2;

const SignalEventList = (props: SignalEventListProps & TrackingProps) => {
    const {
        title,
        onArticleLinkClick,
        onCompanyLinkClick,
        onLinkedInLinkClick,
        onShowDescriptionClick,
        hasShowMoreButton = false,
    } = props;

    const numberOfAllSignals = useSelector(selectNumberOfSignalsFromSource);
    const isSignalsDescriptionLoading = useSelector(selectSignalsDescriptionIsBeingFetched);
    const primarySignalHook = resolvePrimarySignalHook();

    const { data: signalEvents = [], isFetching: isSignalEventsLoading } = useSignalsEventsQuery();
    const isLoading = isSignalEventsLoading || isSignalsDescriptionLoading;

    const { isShowMoreButtonVisible, onShowMoreClick, signalEventsToRender } = useShowMoreSignalEvents({
        signalEvents,
        hasShowMoreButton,
    });

    const { filterValue, filteredSignalEvents, handleFilterChange } = useSignalEventsFilter({
        signalEvents: signalEventsToRender,
    });

    const promoBanner = primarySignalHook ? (
        <StyledSignalIntentWrapper key="signal-promo-banner">
            <SignalPromoBanner hook={primarySignalHook} />
        </StyledSignalIntentWrapper>
    ) : null;

    const renderSignalItems = () => {
        const signalItems = filteredSignalEvents.map((signal, index) => (
            <SignalEventListItem
                key={`signal-${index}`}
                signal={signal}
                onArticleLinkClick={onArticleLinkClick}
                onCompanyLinkClick={onCompanyLinkClick}
                onLinkedInLinkClick={onLinkedInLinkClick}
                onShowDescriptionClick={onShowDescriptionClick}
            />
        ));

        if (!primarySignalHook) {
            return signalItems;
        }

        const insertIndex = Math.min(DEFAULT_BANNER_POSITION, signalItems.length);

        return [...signalItems.slice(0, insertIndex), promoBanner, ...signalItems.slice(insertIndex)];
    };

    const renderSignalEvents = () => {
        if (isLoading) {
            return (
                <StyledSignalsList>
                    {Array.from({ length: 9 }).map((_, index) => (
                        <StyledSignalWrapper key={index}>
                            <LoaderGradient height={113} />
                        </StyledSignalWrapper>
                    ))}
                </StyledSignalsList>
            );
        }

        if (filteredSignalEvents?.length === 0 && !primarySignalHook) {
            return (
                <SignalEventEmptyState
                    isSignalListEmpty={numberOfAllSignals === 0}
                    onManageClick={() =>
                        companyAnalysisTrackingService.signalEvents.trackClickOnManageSignals("no signals banner")
                    }
                />
            );
        }

        return (
            <>
                <StyledSignalsList>{renderSignalItems()}</StyledSignalsList>

                {isShowMoreButtonVisible ? (
                    <StyledSignalsListShowMoreLayout>
                        <Button
                            type="flat"
                            onClick={onShowMoreClick}
                            dataAutomation="signal-events-other-show-more"
                        >
                            <StyledSignalsListShowMoreLabel>
                                {translate("si.account-overview.signals.show-more")}
                            </StyledSignalsListShowMoreLabel>
                        </Button>
                    </StyledSignalsListShowMoreLayout>
                ) : null}
            </>
        );
    };

    return (
        <Box
            background={colorsPalettes.carbon[0]}
            border={`1px solid ${colorsPalettes.carbon[50]}`}
            borderRadius="6px"
            data-automation-loading={isLoading}
            data-automation="content-section"
        >
            <SignalEventListHeader
                title={title}
                isLoading={isLoading}
            />
            <SignalEventFilter
                value={filterValue}
                signalEvents={signalEvents}
                onFilterChange={handleFilterChange}
                isLoading={isLoading}
            />
            {renderSignalEvents()}
        </Box>
    );
};

export default SignalEventList;
